import {
  isPortalAppointmentPaid,
  type PortalAppointmentItem,
  type PortalPaymentKind,
} from '@/hooks/usePortalAppointments';
import type { PatientFacingAppointmentStatus } from '@/utils/appointmentEditability';
import type { PortalIconName } from '@/components/portal/PortalIcon';
import { practitionerDisplayName } from '@/lib/courseStepSlotPicker';

export type PortalChipTone = 'teal' | 'green' | 'amber' | 'red' | 'neutral' | 'coral';

export function durationMinutes(startIso: string, endIso: string): number {
  return Math.max(0, Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60_000));
}

export function statusChip(
  status: PatientFacingAppointmentStatus,
): { tone: PortalChipTone; icon: PortalIconName; label: string } {
  switch (status) {
    case 'upcoming':
    case 'checked_in':
    case 'in_treatment':
      return { tone: 'teal', icon: 'clock', label: 'Kommende' };
    case 'completed':
      return { tone: 'green', icon: 'check', label: 'Afsluttet' };
    case 'cancelled':
      return { tone: 'neutral', icon: 'x', label: 'Aflyst' };
    case 'no_show':
      return { tone: 'amber', icon: 'info', label: 'Udeblevet' };
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function paymentKindLabel(kind: PortalPaymentKind | null): string {
  switch (kind) {
    case 'punch_card':
      return 'klippekort';
    case 'card':
      return 'kort';
    case 'mobilepay':
      return 'MobilePay';
    case 'invoice':
      return 'faktura';
    case 'other':
      return 'anden metode';
    case null:
      return '';
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

export function paymentChip(
  appointment: PortalAppointmentItem,
): { tone: PortalChipTone; icon: PortalIconName; label: string } {
  const paid = isPortalAppointmentPaid(appointment);
  if (paid) {
    const method = paymentKindLabel(appointment.payment_kind);
    return {
      tone: 'green',
      icon: 'check',
      label: method ? `Betalt med ${method}` : 'Betalt',
    };
  }
  if (appointment.payment_timing === 'upon_booking') {
    return { tone: 'amber', icon: 'invoice', label: 'Ikke betalt' };
  }
  return { tone: 'neutral', icon: 'wallet', label: 'Betales ved behandlingen' };
}

export function appointmentRuleText(
  appointment: PortalAppointmentItem,
  deadlineLabel: string,
): string | null {
  if (appointment.display_status !== 'upcoming') return null;
  const paid = isPortalAppointmentPaid(appointment);
  if (paid && !appointment.rescheduling_allowed) {
    return 'Betalte tider ændres via klinikken. Skriv, hvis du vil flytte den.';
  }
  if (appointment.rescheduling_allowed && appointment.cancellation_allowed) {
    return `Flyt eller aflys uden gebyr indtil ${deadlineLabel}.`;
  }
  if (appointment.rescheduling_allowed) {
    return `Flyt uden gebyr indtil ${deadlineLabel}.`;
  }
  if (appointment.cancellation_allowed) {
    return `Aflys uden gebyr indtil ${deadlineLabel}.`;
  }
  return null;
}

/** Same COALESCE(name, email) + UUID strip the forløb page uses. */
export function portalAppointmentHostLabel(
  appointment: Pick<PortalAppointmentItem, 'clinician_name'>,
  fallback = 'Klinikken tildeler behandler',
): string {
  return practitionerDisplayName(appointment.clinician_name, fallback);
}

export function portalUserDisplayName(
  name: string | null | undefined,
  email: string | null | undefined,
): string | null {
  return practitionerDisplayName(name?.trim() || email?.trim() || '', '') || null;
}
